import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Mail, Globe, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../hooks/useBranding";

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { branding } = useBranding();

  const SocialIcons = {
    twitter: Twitter,
    telegram: Mail, // Using Mail as fallback for telegram if not found
    discord: Globe,
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin
  };

  return (
    <footer className="bg-slate-950 text-slate-400 pt-24 pb-12 border-t border-white/5 font-inter">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          {/* Company Info */}
          <div className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="relative group">
                <div className="absolute -inset-1 bg-indigo-500 rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                <div className="relative bg-slate-900 border border-white/10 p-2 rounded-xl shadow-2xl">
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt="Logo" className="h-8" />
                    ) : (
                      <span className="text-xl font-black bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent tracking-tighter">
                          {branding.site_name}
                      </span>
                    )}
                </div>
              </div>
            </div>
            <p className="text-sm font-medium leading-relaxed">
              {branding.footer_about || t('footer.infrastructure_desc')}
            </p>
            <div className="flex gap-3">
              {branding.social_links && Object.entries(branding.social_links).map(([key, url]) => {
                const Icon = SocialIcons[key] || Globe;
                return (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/5 rounded-xl flex items-center justify-center hover:bg-indigo-500/10 hover:text-indigo-400 transition-all duration-300 bg-slate-900/50">
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-black text-white uppercase tracking-[0.2em] mb-8 font-outfit">{t('footer.ecosystem')}</h3>
            <ul className="space-y-4">
              {[
                { name: t('footer.terminal'), path: '/spot' },
                { name: t('footer.invest_packs'), path: '/packs' },
                { name: t('footer.affiliate_network'), path: '/mlm' },
                { name: t('footer.security_protocols'), path: '/security' },
                { name: t('footer.api_docs'), path: '/audits' }
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-sm font-bold hover:text-indigo-400 transition-colors flex items-center gap-2 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-800 group-hover:bg-indigo-500 transition-colors"></div>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Governance & Safety */}
          <div>
            <h3 className="text-xs font-black text-white uppercase tracking-[0.2em] mb-8 font-outfit">{t('footer.governance')}</h3>
            <ul className="space-y-4">
              {[
                { name: t('footer.terms'), path: '/terms' },
                { name: t('footer.risk_disclaimer'), path: '/risk' },
                { name: t('footer.aml_kyc'), path: '/aml' },
                { name: t('footer.asset_protection'), path: '/insurance' },
                { name: t('footer.audit_reports'), path: '/audits' }
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-sm font-bold hover:text-cyan-400 transition-colors flex items-center gap-2 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-800 group-hover:bg-cyan-500 transition-colors"></div>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Institutional Contact */}
          <div>
            <h3 className="text-xs font-black text-white uppercase tracking-[0.2em] mb-8 font-outfit">{t('footer.support')}</h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="mt-1 p-2 bg-indigo-500/10 rounded-lg text-indigo-400">
                    <Mail size={16} />
                </div>
                <div>
                    <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">{t('footer.assistance_247')}</p>
                    <p className="text-sm font-bold text-slate-300">{branding.support_email}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="mt-1 p-2 bg-cyan-500/10 rounded-lg text-cyan-400">
                    <Globe size={16} />
                </div>
                <div>
                    <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">{t('footer.headquarters')}</p>
                    <p className="text-sm font-bold text-slate-300">{branding.office_address}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Compliance Section */}
        <div className="pt-12 border-t border-white/5">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-10">
            <div className="flex flex-col gap-2 max-w-lg">
                <div className="flex items-center gap-4 mb-2">
                    <img src="https://img.icons8.com/color/48/000000/visa.png" alt="Visa" className="h-6 grayscale opacity-30 hover:grayscale-0 hover:opacity-100 transition-all" />
                    <img src="https://img.icons8.com/color/48/000000/mastercard.png" alt="Mastercard" className="h-6 grayscale opacity-30 hover:grayscale-0 hover:opacity-100 transition-all" />
                    <img src="https://img.icons8.com/color/48/000000/bitcoin.png" alt="BTC" className="h-6 grayscale opacity-30 hover:grayscale-0 hover:opacity-100 transition-all" />
                    <div className="h-4 w-px bg-white/10 mx-2"></div>
                    <div className="flex items-center gap-1 text-[10px] font-black text-indigo-400 tracking-tighter">
                        <ShieldCheck size={12} /> PCI COMPLIANT
                    </div>
                </div>
              <p className="text-[11px] font-bold text-slate-600 leading-relaxed uppercase tracking-tighter">
                {t('footer.compliance_text', { year: currentYear })}
              </p>
            </div>

          </div>
        </div>
      </div>
    </footer>
  );
}